import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Groups as GroupsIcon,
  TrendingUp as TrendingIcon,
  FiberNew as NewIcon,
  SortByAlpha as AlphaIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import MobileWidgetDrawer from "../components/MobileWidgetDrawer";
import MobileNavDrawer from "../components/MobileNavDrawer";
import { useTheme } from "../components/ThemeContext";
import { DeviceContext } from "../context/DeviceContext";
import { getImageUrl } from "../utils/imageUrl";

const API_BASE = process.env.REACT_APP_SC_API_BASE_URL || "http://localhost:8080";
const PAGE_SIZE = 20;
const TOPBAR_HEIGHT = 64;

// ── Sort tab ────────────────────────────────────────────────────────────────
function SortTab({ id, label, icon: Icon, active, onClick }) {
  const { theme } = useTheme();
  return (
    <Box
      onClick={() => onClick(id)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        px: "16px",
        py: "8px",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: active ? 600 : 400,
        fontFamily: "Inter, sans-serif",
        color: active ? theme.palette.primary.light : theme.palette.text.secondary,
        backgroundColor: active ? "rgba(5,223,114,0.08)" : "transparent",
        border: `1px solid ${active ? "rgba(5,223,114,0.25)" : "transparent"}`,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
        "&:hover": {
          backgroundColor: active ? "rgba(5,223,114,0.1)" : theme.palette.background.highlight,
          color: active ? theme.palette.primary.light : theme.palette.text.primary,
        },
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
      {label}
    </Box>
  );
}

// ── Community card ──────────────────────────────────────────────────────────
function CommunityCard({ community, onClick }) {
  const { theme } = useTheme();
  const hasBanner = Boolean(community.banner);
  const hasIcon   = Boolean(community.icon);

  const initial = (community.name || "?")[0].toUpperCase();
  const hue     = community.name
    ? community.name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 360
    : 160;

  return (
    <Box
      onClick={() => onClick(community.name)}
      sx={{
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: "pointer",
        transition: "border-color 0.2s, transform 0.15s",
        "&:hover": {
          borderColor: theme.palette.border.green,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Banner */}
      <Box
        sx={{
          height: 64,
          backgroundColor: hasBanner ? "transparent" : `hsl(${hue},40%,18%)`,
          backgroundImage: hasBanner ? `url(${getImageUrl(community.banner)})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Icon + info */}
      <Box sx={{ px: "16px", pb: "16px" }}>
        {/* Icon (overlaps banner) */}
        <Box
          sx={{
            mt: "-20px",
            mb: "8px",
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: `2px solid ${theme.palette.background.default}`,
            overflow: "hidden",
            backgroundColor: hasIcon ? "transparent" : `hsl(${hue},50%,25%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {hasIcon ? (
            <Box
              component="img"
              src={getImageUrl(community.icon)}
              alt={community.name}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>
              {initial}
            </Typography>
          )}
        </Box>

        {/* Name */}
        <Typography
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "15px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: "4px",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {community.name}
        </Typography>

        {/* Description */}
        {community.description && (
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "12px",
              color: theme.palette.text.secondary,
              lineHeight: 1.5,
              mb: "10px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {community.description}
          </Typography>
        )}

        {/* Followers */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <GroupsIcon sx={{ fontSize: 13, color: theme.palette.text.muted }} />
          <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px", color: theme.palette.text.muted }}>
            {(community.followersCount || 0).toLocaleString()} members
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Card skeleton ────────────────────────────────────────────────────────────
function CardSkeleton() {
  const { theme } = useTheme();
  return (
    <Box sx={{ borderRadius: "12px", overflow: "hidden", border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
      <Skeleton variant="rectangular" height={64} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
      <Box sx={{ px: 2, pb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mt: "-20px", mb: 1, bgcolor: "rgba(255,255,255,0.06)" }} />
        <Skeleton width="60%" height={18} sx={{ mb: 0.5, bgcolor: "rgba(255,255,255,0.06)" }} />
        <Skeleton width="90%" height={14} sx={{ mb: 0.5, bgcolor: "rgba(255,255,255,0.06)" }} />
        <Skeleton width="75%" height={14} sx={{ mb: 1, bgcolor: "rgba(255,255,255,0.06)" }} />
        <Skeleton width="40%" height={12} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
      </Box>
    </Box>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function CommunitiesPage() {
  const { theme } = useTheme();
  const navigate  = useNavigate();
  const { isMobile } = useContext(DeviceContext);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sort, setSort]               = useState("trending");
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]         = useState(true);
  const [search, setSearch]           = useState("");

  const loaderRef    = useRef(null);
  // Refs avoid stale-closure bugs in the IntersectionObserver callback
  const pageRef      = useRef(0);
  const fetchingRef  = useRef(false);
  const sortRef      = useRef(sort);

  // Fetch a page — replace=true resets the list (sort change)
  const fetchPage = useCallback(async (sortVal, pageNum, replace) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (replace) setLoading(true);
    else setLoadingMore(true);
    try {
      const res  = await fetch(`${API_BASE}/api/community/all?sort=${sortVal}&page=${pageNum}&size=${PAGE_SIZE}`);
      const data = await res.json();
      setCommunities((prev) => replace ? data.communities : [...prev, ...data.communities]);
      setHasMore(data.hasMore);
      pageRef.current = pageNum;
    } catch (e) {
      console.error("Failed to fetch communities:", e);
    } finally {
      fetchingRef.current = false;
      if (replace) setLoading(false);
      else setLoadingMore(false);
    }
  }, []);

  // Reset and reload on sort change
  useEffect(() => {
    sortRef.current = sort;
    pageRef.current = 0;
    setCommunities([]);
    setHasMore(true);
    setLoading(true);
    fetchPage(sort, 0, true);
  }, [sort, fetchPage]);

  // Infinite scroll — reads page/sort from refs, no stale closure
  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchingRef.current) {
          setHasMore((more) => {
            if (more) fetchPage(sortRef.current, pageRef.current + 1, false);
            return more;
          });
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, fetchPage]); // re-attach when loading finishes (loaderRef mounts)

  const handleCardClick = (name) => navigate(`/community/${name}`);

  const sortTabs = [
    { id: "trending",     label: "Trending", icon: TrendingIcon },
    { id: "new",          label: "New",       icon: NewIcon      },
    { id: "alphabetical", label: "A – Z",     icon: AlphaIcon    },
  ];

  // Client-side search filter
  const visible = search.trim()
    ? communities.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : communities;

  return (
    <>
      <ResponsiveTopBar isMobile={isMobile} onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)} />
      <MobileNavDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      <Box
        sx={{
          mt: `${TOPBAR_HEIGHT}px`,
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          pb: isMobile ? "6rem" : 6,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            px: { xs: 2, md: 4 },
            py: 3,
          }}
        >
          <Box sx={{ maxWidth: 1100, mx: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: 2 }}>
              <GroupsIcon sx={{ fontSize: 28, color: theme.palette.primary.light }} />
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "22px", md: "28px" },
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                Communities
              </Typography>
            </Box>

            {/* Sort tabs + search row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {sortTabs.map((t) => (
                <SortTab key={t.id} {...t} active={sort === t.id} onClick={setSort} />
              ))}

              {/* Search */}
              <Box
                sx={{
                  ml: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "8px",
                  px: "12px",
                  py: "6px",
                  minWidth: { xs: "100%", sm: 220 },
                  order: { xs: 1, sm: 0 },
                }}
              >
                <SearchIcon sx={{ fontSize: 16, color: theme.palette.text.muted, flexShrink: 0 }} />
                <Box
                  component="input"
                  placeholder="Filter communities…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    border: "none",
                    outline: "none",
                    backgroundColor: "transparent",
                    fontSize: "14px",
                    fontFamily: "Inter, sans-serif",
                    color: theme.palette.text.primary,
                    width: "100%",
                    "::placeholder": { color: theme.palette.text.muted },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Grid ── */}
        <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, md: 4 }, pt: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            {loading
              ? Array.from({ length: PAGE_SIZE }).map((_, i) => <CardSkeleton key={i} />)
              : visible.map((c) => (
                  <CommunityCard key={c.id} community={c} onClick={handleCardClick} />
                ))}
          </Box>

          {/* Load more trigger */}
          {!loading && (
            <Box ref={loaderRef} sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              {loadingMore && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                      lg: "repeat(4, 1fr)",
                    },
                    gap: 2,
                    width: "100%",
                  }}
                >
                  {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
                </Box>
              )}
              {!hasMore && communities.length > 0 && !search && (
                <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "13px", color: theme.palette.text.muted }}>
                  All {communities.length} communities loaded
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {isMobile && <MobileWidgetDrawer />}
    </>
  );
}
